import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { showErrorNotification } from '@vision/ui/components';
import {
  WorkflowConditionType,
  WorkflowDisplayMode,
  WorkflowEventType,
  WorkflowTaskCampaignType,
  WorkflowTaskDetailType,
  WorkflowType,
} from '@vision/ui/enums';
import i18n from '@vision/ui/i18n';
import {
  Nullable,
  SortableList,
  Workflow,
  WorkflowCondition,
  WorkflowConditionRow,
  WorkflowSettings,
  WorkflowTask,
  WorkflowTaskCollectionRuleContainer,
} from '@vision/ui/interfaces';
import { WorkflowsService } from '@vision/ui/services';
import {
  createWorkflowClone,
  getTicketEventProjectIdFromConditions,
  getTicketEventSettingsFromConditions,
  omit,
  uuid,
} from '@vision/ui/utils';

export type WorkflowDefaultTaskParameters = {
  workflowTask: Nullable<WorkflowTask>;
  selectedTaskDetailType: Nullable<WorkflowTaskDetailType>;
};

export interface WorkflowsStateType {
  addConditionPanelOpened: boolean;
  addFilterPanelOpened: boolean;
  addTaskPanelOpened: boolean;
  addWorkflowModalOpened: boolean;
  currentNode: string;
  defaultTaskParameters?: WorkflowDefaultTaskParameters;
  displayMode: WorkflowDisplayMode;
  editingCondition: WorkflowConditionRow;
  editingFilter: WorkflowTaskCollectionRuleContainer;
  editingTask: WorkflowTask;
  isDirty: boolean;
  managingWorkflow: Workflow;
  taskValidationRequestedKey: string;
  workflows: Workflow[];
}

const initialState: WorkflowsStateType = {
  addConditionPanelOpened: false,
  addFilterPanelOpened: false,
  addTaskPanelOpened: false,
  addWorkflowModalOpened: false,
  currentNode: null,
  displayMode: null,
  editingCondition: null,
  editingFilter: null,
  editingTask: null,
  isDirty: false,
  managingWorkflow: null,
  taskValidationRequestedKey: null,
  workflows: null,
};

const DIRTY_ACTION_TYPES = new Set([
  'WorkflowsState/moveCondition',
  'WorkflowsState/moveFilter',
  'WorkflowsState/removeCondition',
  'WorkflowsState/removeFilter',
  'WorkflowsState/renameTask',
  'WorkflowsState/reorderTasks',
  'WorkflowsState/saveCondition',
  'WorkflowsState/saveFilter',
  'WorkflowsState/saveSettings',
  'WorkflowsState/saveTask',
]);

const CLEAN_ACTION_TYPES = new Set([
  'WorkflowsState/onWorkflowPublished',
  'WorkflowsState/resetManagingWorkflow',
  'WorkflowsState/setViewingWorkflow',
]);

function clearTicketEventTaskSelections(workflow: Workflow) {
  workflow.tasks = (workflow.tasks || []).map((task) => {
    if (task.action.detail_type === WorkflowTaskDetailType.CHANGE_TICKET_STATUS) {
      delete task.action.ticket_status_id;
      delete task.action.ticket_status;
      delete task.action.ticket_status_name;
    }

    if (task.action.detail_type === WorkflowTaskDetailType.PUSH_TICKET_TO_QUEUE) {
      delete task.action.ticket_queue_id;
      delete task.action.ticket_queue;
      delete task.action.ticket_queue_name;
    }

    return task;
  });
}

export const WorkflowsState = createSlice({
  name: 'WorkflowsState',
  initialState,
  reducers: {
    initializeWorkflow: (
      state,
      action: PayloadAction<{
        accountId: string;
        selectedCampaignId: string;
        selectedCampaignType: WorkflowTaskCampaignType;
        selectedWorkflowId: string;
        name: string;
        event_type: WorkflowEventType;
        type: WorkflowType;
      }>,
    ) => {
      const {
        payload: { accountId, selectedCampaignId, selectedCampaignType, selectedWorkflowId, name, event_type, type },
      } = action;

      const isTypeOrEventTypeChanged =
        !!state.managingWorkflow &&
        (state.managingWorkflow.type !== type || state.managingWorkflow.event_type !== event_type);

      const hasWorkflowChanges =
        !state.managingWorkflow ||
        isTypeOrEventTypeChanged ||
        state.managingWorkflow.name !== name ||
        state.managingWorkflow.selectedCampaignId !== selectedCampaignId ||
        state.managingWorkflow.selectedWorkflowId !== selectedWorkflowId;

      const workflow: Workflow = {
        id: state.managingWorkflow?.id || uuid(),
        account_id: accountId,
        event_type,
        name,
        selectedCampaignId,
        selectedCampaignType,
        selectedWorkflowId,
        type,
      };

      if (!state.managingWorkflow || isTypeOrEventTypeChanged) {
        workflow.conditions = { or: { conditions: [] } };
        workflow.tasks = [];
        workflow.settings = null;
      } else {
        workflow.conditions = state.managingWorkflow.conditions;
        workflow.tasks = state.managingWorkflow.tasks;
        workflow.settings = state.managingWorkflow.settings;
      }

      if (
        event_type === WorkflowEventType.WORKFLOW_COMPLETED_EVENT ||
        type == WorkflowType.SCHEDULED ||
        type === WorkflowType.TICKET_EVENT ||
        (type == WorkflowType.AUTOMATION && !state.managingWorkflow)
      ) {
        state.addConditionPanelOpened = true;
      }

      state.managingWorkflow = workflow;
      state.isDirty = state.displayMode === WorkflowDisplayMode.CLONE || hasWorkflowChanges;
    },
    resetManagingWorkflow: (state) => {
      state.managingWorkflow = null;
      state.displayMode = null;
      state.taskValidationRequestedKey = null;
    },
    setExecutionPauseExpiresAt: (state, action: PayloadAction<{ expiresAt: string }>) => {
      if (state.managingWorkflow) {
        state.managingWorkflow.execution_pause_expires_at = action.payload.expiresAt;
      }
    },
    setViewingWorkflow: (state, action: PayloadAction<{ workflow: Workflow }>) => {
      const { workflow } = action.payload;

      if (workflow) {
        state.displayMode = WorkflowDisplayMode.PREVIEW;
        state.managingWorkflow = workflow;
      } else {
        state.displayMode = WorkflowDisplayMode.CREATE;
        state.managingWorkflow = null;
      }
    },
    openAddWorkflowModal: (state) => {
      state.addWorkflowModalOpened = true;
    },
    closeAddWorkflowModal: (state) => {
      state.addWorkflowModalOpened = false;
    },
    openAddConditionPanel: (state, action: PayloadAction<{ appendTo?: string; task?: WorkflowTask }>) => {
      const {
        payload: { appendTo, task },
      } = action;

      state.editingCondition = null;
      state.editingTask = task ?? null;
      state.currentNode = appendTo;
      state.addTaskPanelOpened = false;
      state.addFilterPanelOpened = false;
      state.addConditionPanelOpened = true;
      state.taskValidationRequestedKey = null;
    },
    closeAddConditionPanel: (state) => {
      state.addConditionPanelOpened = false;
    },
    openAddTaskPanel: (state) => {
      state.editingTask = null;
      state.editingCondition = null;
      state.currentNode = null;
      state.addConditionPanelOpened = false;
      state.addFilterPanelOpened = false;
      state.addTaskPanelOpened = true;
      state.taskValidationRequestedKey = null;
    },
    openAddBranchIfFilterTaskPanel: (state, action: PayloadAction<{ task: WorkflowTask; appendTo?: string }>) => {
      state.editingTask = action.payload.task;
      state.editingCondition = null;
      state.currentNode = action.payload.appendTo ?? '';
      state.addConditionPanelOpened = false;
      state.addFilterPanelOpened = false;
      state.addTaskPanelOpened = true;
      state.taskValidationRequestedKey = null;
    },
    closeAddTaskPanel: (state) => {
      state.addTaskPanelOpened = false;
      state.taskValidationRequestedKey = null;
    },
    openAddFilterPanel: (state, action: PayloadAction<{ task: WorkflowTask; appendTo?: string }>) => {
      const {
        payload: { appendTo, task },
      } = action;

      state.editingTask = task;
      state.editingCondition = null;
      state.currentNode = appendTo;
      state.editingFilter = null;
      state.addConditionPanelOpened = false;
      state.addTaskPanelOpened = false;
      state.addFilterPanelOpened = true;
      state.taskValidationRequestedKey = null;
    },
    closeAddFilterPanel: (state) => {
      state.addFilterPanelOpened = false;
    },
    saveCondition: (
      state,
      action: PayloadAction<{
        conditions: WorkflowCondition[];
        conditionType: WorkflowConditionType;
        closeAddConditionPanel?: boolean;
        closeAddTaskPanel?: boolean;
        parentId?: string;
        replaceConditions?: boolean;
        taskKey?: string;
      }>,
    ) => {
      const {
        payload: {
          conditions,
          conditionType,
          closeAddConditionPanel = true,
          closeAddTaskPanel = false,
          parentId,
          replaceConditions = false,
          taskKey,
        },
      } = action;
      const branchTaskKey =
        taskKey ??
        (state.editingTask?.action.detail_type === WorkflowTaskDetailType.BRANCH_IF
          ? state.editingTask.key
          : undefined);
      const branchTask = branchTaskKey ? state.managingWorkflow.tasks.find((task) => task.key === branchTaskKey) : null;
      if (branchTask && !branchTask.action.conditions) {
        branchTask.action.conditions = { or: { conditions: [] } };
      }
      const conditionRoot = branchTask ? branchTask.action.conditions : state.managingWorkflow.conditions;
      const currentTicketEventProjectId =
        !branchTask && state.managingWorkflow.type === WorkflowType.TICKET_EVENT
          ? getTicketEventProjectIdFromConditions(state.managingWorkflow.conditions)
          : '';

      const conditionRow: WorkflowConditionRow[] = [
        {
          id: state.editingCondition?.id || uuid(),
          conditionType: conditionType,
          and: { conditions },
        },
      ];

      const targetParentId = taskKey ? parentId : state.currentNode;
      const targetNode = conditionRoot.or.conditions.find((og) => og.id === targetParentId);

      if (targetNode) {
        if (state.editingCondition) {
          const targetCondition = targetNode.and.conditions.find((c) => c.id === state.editingCondition.id);
          targetNode.and.conditions.splice(targetNode.and.conditions.indexOf(targetCondition), 1, ...conditionRow);
        } else {
          targetNode.and.conditions.push(...conditionRow);
        }
      } else {
        if (replaceConditions) conditionRoot.or.conditions = [];

        conditionRoot.or.conditions.push({
          id: uuid(),
          and: { id: uuid(), conditions: conditionRow },
        });
      }

      state.currentNode = null;
      state.editingCondition = null;
      state.editingTask = null;

      if (!branchTask && state.managingWorkflow.type === WorkflowType.TICKET_EVENT) {
        const nextTicketEventProjectId = getTicketEventProjectIdFromConditions(state.managingWorkflow.conditions);

        if (
          currentTicketEventProjectId &&
          nextTicketEventProjectId &&
          currentTicketEventProjectId !== nextTicketEventProjectId
        ) {
          clearTicketEventTaskSelections(state.managingWorkflow);
        }
      }

      if (closeAddConditionPanel) state.addConditionPanelOpened = false;
      if (closeAddTaskPanel) state.addTaskPanelOpened = false;
    },
    editCondition: (
      state,
      action: PayloadAction<{ parentId: string; condition: WorkflowConditionRow; task?: WorkflowTask }>,
    ) => {
      const {
        payload: { parentId, condition, task },
      } = action;

      state.editingTask = task ?? null;
      state.taskValidationRequestedKey = null;
      state.editingCondition = condition;
      state.currentNode = parentId;
      state.addTaskPanelOpened = false;
      state.addConditionPanelOpened = true;
    },
    removeCondition: (state, action: PayloadAction<{ parentId: string; id: string; taskKey?: string }>) => {
      const {
        payload: { parentId, id, taskKey },
      } = action;
      const branchTask = taskKey ? state.managingWorkflow.tasks.find((task) => task.key === taskKey) : null;
      const conditionRoot = branchTask ? branchTask.action.conditions : state.managingWorkflow.conditions;
      if (!conditionRoot) return;
      const currentTicketEventProjectId =
        !branchTask && state.managingWorkflow.type === WorkflowType.TICKET_EVENT
          ? getTicketEventProjectIdFromConditions(state.managingWorkflow.conditions)
          : '';

      const parentNode = conditionRoot.or.conditions.find((og) => og.id === parentId);
      const targetNode = parentNode.and.conditions.find((c) => c.id === id);

      parentNode.and.conditions.splice(parentNode.and.conditions.indexOf(targetNode), 1);

      if (!targetNode.and.conditions.length) {
        parentNode.and.conditions.splice(parentNode.and.conditions.indexOf(targetNode), 1);
      }

      if (!parentNode.and.conditions.length) {
        conditionRoot.or.conditions.splice(conditionRoot.or.conditions.indexOf(parentNode), 1);
      }

      if (!branchTask && state.managingWorkflow.type === WorkflowType.TICKET_EVENT) {
        const nextTicketEventProjectId = getTicketEventProjectIdFromConditions(state.managingWorkflow.conditions);

        if (currentTicketEventProjectId && currentTicketEventProjectId !== nextTicketEventProjectId) {
          clearTicketEventTaskSelections(state.managingWorkflow);
        }
      }
    },
    saveTask: (
      state,
      action: PayloadAction<{ task: WorkflowTask; closeAddTaskPanel?: boolean; replaceTasks?: boolean }>,
    ) => {
      const {
        payload: { task, closeAddTaskPanel = true, replaceTasks: replaceTasks = false },
      } = action;
      task.key = state.editingTask?.key || uuid();

      if (state.editingTask) {
        const targetTask = state.managingWorkflow.tasks.find((t) => t.key === state.editingTask.key);

        // If form did not explicitly provide collections (undefined), preserve the
        // store's current collections (may have been modified by add/edit/remove
        // filter actions). If form provided a real array (even empty), use it.
        if (task.action.collections === undefined) {
          task.action.collections = targetTask.action.collections;
        }

        Object.assign(targetTask, task);
      } else {
        if (replaceTasks) state.managingWorkflow.tasks = [];

        const existingTaskNames = state.managingWorkflow.tasks.map((t) => t.name);
        const taskName = task.name;

        let taskNameCount = 1;

        while (existingTaskNames.includes(task.name)) {
          taskNameCount++;
          task.name = `${taskName} (${taskNameCount})`;
        }

        state.managingWorkflow.tasks.push(task);
      }

      state.editingTask = null;
      state.taskValidationRequestedKey = null;

      if (closeAddTaskPanel) state.addTaskPanelOpened = false;
    },
    editTask: (state, action: PayloadAction<{ task: WorkflowTask }>) => {
      const {
        payload: { task },
      } = action;

      state.editingCondition = null;
      state.currentNode = null;
      state.editingTask = task;
      state.addFilterPanelOpened = false;
      state.editingFilter = null;
      state.addTaskPanelOpened = true;
      state.addConditionPanelOpened = false;
      state.taskValidationRequestedKey = null;
    },
    editTaskWithValidation: (state, action: PayloadAction<{ task: WorkflowTask }>) => {
      const { task } = action.payload;

      state.editingCondition = null;
      state.currentNode = null;
      state.editingTask = task;
      state.addFilterPanelOpened = false;
      state.editingFilter = null;
      state.addTaskPanelOpened = true;
      state.addConditionPanelOpened = false;
      state.taskValidationRequestedKey = task.key;
    },
    removeTask: (state, action: PayloadAction<{ key: string }>) => {
      const targetTaskKey = action.payload.key;
      const targetTask = state.managingWorkflow.tasks.find((t) => t.key === targetTaskKey);

      const usingTask = state.managingWorkflow.tasks.find(
        (task) => task.action && task.action.source === targetTaskKey,
      );

      if (usingTask) {
        showErrorNotification({
          message: i18n.t('workflows:workflowEditor.taskInUseAsSource', {
            targetTask: { name: targetTask.name },
            usingTask: { name: usingTask.name },
          }),
        });
        return;
      }

      state.managingWorkflow.tasks.splice(state.managingWorkflow.tasks.indexOf(targetTask), 1);

      // Explicit: removeTask can early-return without changes, so the matcher can't handle this safely.
      state.isDirty = true;
    },
    renameTask: (state, action: PayloadAction<{ key: string; name: string }>) => {
      const {
        payload: { key, name },
      } = action;

      const targetTask = state.managingWorkflow.tasks.find((t) => t.key === key);

      targetTask.name = name;
    },
    saveFilter: (
      state,
      action: PayloadAction<{ closeAddFilterPanel?: boolean; filter: WorkflowTaskCollectionRuleContainer }>,
    ) => {
      const {
        payload: { closeAddFilterPanel = true, filter },
      } = action;
      filter.id = state.editingFilter?.id || uuid();

      if (state.editingFilter) {
        const targetTask = state.managingWorkflow.tasks.find((t) => t.key === state.editingTask.key);
        const targetCollection = targetTask.action.collections.find((c) => c.id === state.currentNode);
        const targetFilter = targetCollection.rules.find((f) => f.id === state.editingFilter.id);

        Object.assign(targetFilter, filter);
      } else {
        if (state.currentNode) {
          const targetCollection = state.editingTask.action.collections.find((c) => c.id === state.currentNode);
          targetCollection.rules.push(filter);
        } else {
          state.editingTask.action.collections.push({
            id: uuid(),
            rules: [filter],
          });
        }

        const targetTask = state.managingWorkflow.tasks.find((t) => t.key === state.editingTask.key);
        Object.assign(targetTask, state.editingTask);
      }

      state.currentNode = null;
      state.editingFilter = null;

      if (closeAddFilterPanel) state.addFilterPanelOpened = false;
    },
    editFilter: (
      state,
      action: PayloadAction<{ task: WorkflowTask; collectionId: string; filter: WorkflowTaskCollectionRuleContainer }>,
    ) => {
      const {
        payload: { collectionId, filter, task },
      } = action;

      state.editingTask = task;
      state.editingCondition = null;
      state.editingFilter = filter;
      state.currentNode = collectionId;
      state.addConditionPanelOpened = false;
      state.addTaskPanelOpened = false;
      state.addFilterPanelOpened = true;
      state.taskValidationRequestedKey = null;
    },
    removeFilter: (state, action: PayloadAction<{ taskKey: string; collectionId: string; ruleId: string }>) => {
      const {
        payload: { taskKey, collectionId, ruleId },
      } = action;

      const targetTask = state.managingWorkflow.tasks.find((t) => t.key === taskKey);
      const targetCollectionIndex = targetTask.action.collections.findIndex((c) => c.id === collectionId);
      const targetCollection = targetTask.action.collections[targetCollectionIndex];
      const targetFilterIndex = targetCollection.rules.findIndex((f) => f.id === ruleId);

      targetCollection.rules.splice(targetFilterIndex, 1);

      if (targetCollection.rules.length === 0) {
        targetTask.action.collections.splice(targetCollectionIndex, 1);
      }
    },
    saveSettings: (state, action: PayloadAction<{ settings: WorkflowSettings }>) => {
      const currentSettings =
        state.managingWorkflow.settings || getTicketEventSettingsFromConditions(state.managingWorkflow.conditions);
      const currentProjectId = currentSettings?.ticket_project_id;
      const nextProjectId = action.payload.settings.ticket_project_id;

      state.managingWorkflow.settings = action.payload.settings;

      if (
        state.managingWorkflow.type === WorkflowType.TICKET_EVENT &&
        currentProjectId &&
        nextProjectId &&
        currentProjectId !== nextProjectId
      ) {
        clearTicketEventTaskSelections(state.managingWorkflow);
      }

      state.addConditionPanelOpened = false;
    },
    editSettings: (state) => {
      state.editingTask = null;
      state.editingFilter = null;
      state.editingCondition = null;
      state.currentNode = null;
      state.addTaskPanelOpened = false;
      state.addConditionPanelOpened = true;
      state.taskValidationRequestedKey = null;
    },
    onWorkflowPublished: (state) => {
      state.addConditionPanelOpened = false;
      state.addTaskPanelOpened = false;
      state.addWorkflowModalOpened = false;
      state.currentNode = null;
      state.displayMode = null;
      state.editingCondition = null;
      state.editingTask = null;
      state.managingWorkflow = null;
      state.taskValidationRequestedKey = null;
    },
    resetManagingContent: (state) => {
      state.addConditionPanelOpened = false;
      state.addTaskPanelOpened = false;
      state.addFilterPanelOpened = false;
      state.currentNode = null;
      state.editingCondition = null;
      state.editingFilter = null;
      state.editingTask = null;
      state.defaultTaskParameters = undefined;
      state.taskValidationRequestedKey = null;
    },
    moveCondition: (
      state,
      action: PayloadAction<{ id: string; sourceGroupId: string; targetGroupId: string; taskKey?: string }>,
    ) => {
      const {
        payload: { id, sourceGroupId, targetGroupId, taskKey },
      } = action;

      if (sourceGroupId !== targetGroupId) {
        const branchTask = taskKey ? state.managingWorkflow.tasks.find((task) => task.key === taskKey) : null;
        const conditionRoot = branchTask ? branchTask.action.conditions : state.managingWorkflow.conditions;
        if (!conditionRoot) return;
        const sourceGroup = conditionRoot.or.conditions.find((g) => g.and.id === sourceGroupId);
        const targetGroup = conditionRoot.or.conditions.find((g) => g.and.id === targetGroupId);

        const condition = sourceGroup.and.conditions.find((c) => c.id === id);

        sourceGroup.and.conditions.splice(sourceGroup.and.conditions.indexOf(condition), 1);

        if (sourceGroup.and.conditions.length === 0) {
          conditionRoot.or.conditions.splice(conditionRoot.or.conditions.indexOf(sourceGroup), 1);
        }

        targetGroup.and.conditions.push(condition);
      }
    },
    moveFilter: (
      state,
      action: PayloadAction<{ taskKey: string; id: string; sourceGroupId: string; targetGroupId: string }>,
    ) => {
      const {
        payload: { taskKey, id, sourceGroupId, targetGroupId },
      } = action;

      if (sourceGroupId !== targetGroupId) {
        const sourceTask = state.managingWorkflow.tasks.find((t) => t.key === taskKey);

        const sourceCollection = sourceTask.action.collections.find((c) => c.id === sourceGroupId);
        const targetCollection = sourceTask.action.collections.find((c) => c.id === targetGroupId);

        const rule = sourceCollection.rules.find((c) => c.id === id);

        sourceCollection.rules.splice(sourceCollection.rules.indexOf(rule), 1);

        if (sourceCollection.rules.length === 0) {
          sourceTask.action.collections.splice(sourceTask.action.collections.indexOf(sourceCollection), 1);
        }

        targetCollection.rules.push(rule);
      }
    },
    reorderTasks: (state, action: PayloadAction<{ tasks: SortableList<WorkflowTask> }>) => {
      state.managingWorkflow.tasks = action.payload.tasks.map((task) => omit(task, 'sortId'));
    },
    setDisplayMode: (state, action: PayloadAction<WorkflowDisplayMode>) => {
      state.displayMode = action.payload;
    },
    setDefaultTaskParameters: (state, action: PayloadAction<WorkflowDefaultTaskParameters | undefined>) => {
      state.defaultTaskParameters = action.payload;
    },
    setWorkflowDirty: (state, action: PayloadAction<boolean>) => {
      state.isDirty = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(WorkflowsService.endpoints.workflow.matchFulfilled, (state, action) => {
      if (state.displayMode === WorkflowDisplayMode.CLONE) {
        state.managingWorkflow = createWorkflowClone(action.payload.data, i18n.t('workflows:cloneNameSuffix'));
        state.isDirty = true;
      } else {
        state.managingWorkflow = action.payload.data;
        state.isDirty = false;
      }
    });
    builder.addMatcher(
      (action) => CLEAN_ACTION_TYPES.has(action.type),
      (state) => {
        state.isDirty = false;
      },
    );
    builder.addMatcher(
      (action) => DIRTY_ACTION_TYPES.has(action.type),
      (state) => {
        state.isDirty = true;
      },
    );
    builder.addMatcher(WorkflowsService.endpoints.workflow.matchRejected, () => {
      setViewingWorkflow({ workflow: null });
    });
  },
});

export const {
  closeAddConditionPanel,
  closeAddTaskPanel,
  closeAddWorkflowModal,
  editCondition,
  editFilter,
  editSettings,
  editTask,
  editTaskWithValidation,
  initializeWorkflow,
  moveCondition,
  moveFilter,
  onWorkflowPublished,
  openAddBranchIfFilterTaskPanel,
  openAddConditionPanel,
  openAddFilterPanel,
  openAddTaskPanel,
  openAddWorkflowModal,
  removeCondition,
  removeFilter,
  removeTask,
  renameTask,
  reorderTasks,
  resetManagingContent,
  resetManagingWorkflow,
  saveCondition,
  saveFilter,
  saveSettings,
  saveTask,
  setDisplayMode,
  setDefaultTaskParameters,
  setExecutionPauseExpiresAt,
  setWorkflowDirty,
  setViewingWorkflow,
} = WorkflowsState.actions;
